'use client';

import React from 'react';
import { Box, Container, Typography } from '@mui/material';

const GOLD = '#FFD400';
const BG_DARK = '#1A1A1A';
const BG_MID = '#242424';

interface PageHeroProps {
  title: string;
  subtitle: string;
  /** Large icon rendered as a faint watermark on the right */
  icon?: React.ReactNode;
  /** Short section label shown above the title (e.g. "People", "Governance") */
  label?: string;
  minHeight?: string;
}

export default function PageHero({
  title,
  subtitle,
  icon,
  label,
  minHeight = '32vh',
}: PageHeroProps) {
  return (
    <Box
      sx={{
        minHeight,
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${BG_DARK} 0%, ${BG_MID} 50%, ${BG_DARK} 100%)`,
        // Diagonal gold hairline texture
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: `repeating-linear-gradient(
            -45deg,
            transparent 0px,
            transparent 40px,
            rgba(255, 212, 0, 0.025) 40px,
            rgba(255, 212, 0, 0.025) 41px
          )`,
          zIndex: 0,
        },
        // Large outer ring — top-right
        '&::after': {
          content: '""',
          position: 'absolute',
          width: '620px',
          height: '620px',
          borderRadius: '50%',
          border: '1px solid rgba(255, 212, 0, 0.08)',
          top: '-230px',
          right: '-170px',
          zIndex: 0,
        },
      }}
    >
      {/* Wingtip mark — top-right (echoes the Dal wingtip on the home hero) */}
      <Box
        sx={{
          position: 'absolute',
          width: '420px',
          height: '420px',
          borderRadius: '30% 30% 30% 8%',
          border: '1px solid rgba(255, 212, 0, 0.09)',
          top: '-140px',
          right: '-110px',
          transform: 'rotate(-12deg)',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      {/* Small wingtip — bottom-left */}
      <Box
        sx={{
          position: 'absolute',
          width: '300px',
          height: '300px',
          borderRadius: '30% 8% 30% 30%',
          border: '1px solid rgba(255, 212, 0, 0.055)',
          bottom: '-110px',
          left: '-80px',
          transform: 'rotate(8deg)',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      {/* Thin gold diagonal slash accent — top-right corner */}
      <Box
        sx={{
          position: 'absolute',
          width: '2px',
          height: '140px',
          background: `linear-gradient(to bottom, transparent, rgba(255,212,0,0.25), transparent)`,
          top: '20px',
          right: { xs: '40px', md: '200px' },
          transform: 'rotate(30deg)',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      {/* Watermark icon */}
      {icon && (
        <Box
          sx={{
            position: 'absolute',
            right: { xs: '-10px', md: '60px' },
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 0,
            color: GOLD,
            opacity: 0.055,
            lineHeight: 0,
            pointerEvents: 'none',
            '& .MuiSvgIcon-root': {
              fontSize: { xs: '130px', md: '170px' },
            },
          }}
        >
          {icon}
        </Box>
      )}

      {/* Content */}
      <Container
        maxWidth="lg"
        sx={{ position: 'relative', zIndex: 1, py: { xs: 4, md: 5.5 } }}
      >
        {/* Section label — rendered as a code comment in monospace */}
        {label && (
          <Typography
            component="div"
            sx={{
              fontFamily: 'var(--font-jetbrains-mono), ui-monospace, SFMono-Regular, Menlo, monospace',
              color: GOLD,
              fontSize: '0.78rem',
              fontWeight: 500,
              letterSpacing: '0.04em',
              textTransform: 'lowercase',
              lineHeight: 1,
              mb: 2,
              opacity: 0.85,
            }}
          >
            <Box component="span" sx={{ color: 'rgba(255, 212, 0, 0.55)', mr: 0.75 }}>
              //
            </Box>
            {label}
          </Typography>
        )}

        {/* Gold accent bar */}
        <Box
          sx={{
            width: '44px',
            height: '3px',
            background: GOLD,
            borderRadius: '2px',
            mb: 2.5,
          }}
        />

        {/* Title */}
        <Typography
          component="h1"
          sx={{
            fontSize: { xs: '1.85rem', sm: '2.25rem', md: '2.7rem' },
            fontWeight: 700,
            color: '#FFFFFF',
            lineHeight: 1.15,
            mb: 1.5,
            letterSpacing: '-0.02em',
            maxWidth: { md: icon ? '65%' : '80%' },
          }}
        >
          {title}
        </Typography>

        {/* Subtitle — minHeight reserves space for two lines so that single-line
            subtitles (e.g. research-themes) produce the same hero height as
            wrapped two-line subtitles (e.g. program-committee, advisory-board).
            Eliminates the height jump between pages. */}
        <Typography
          component="p"
          sx={{
            fontSize: { xs: '0.95rem', md: '1.05rem' },
            color: 'rgba(255, 255, 255, 0.60)',
            maxWidth: { xs: '100%', md: icon ? '55%' : '680px' },
            lineHeight: 1.7,
            fontWeight: 400,
            // Reserved line count × line-height × font-size. 3 lines on
            // mobile (narrow widths wrap more) and 2 on desktop covers all
            // current subtitles without clipping. Keep in sync with the
            // fontSize/lineHeight above if those change.
            minHeight: { xs: 'calc(0.95rem * 1.7 * 3)', md: 'calc(1.05rem * 1.7 * 2)' },
          }}
        >
          {subtitle}
        </Typography>
      </Container>
    </Box>
  );
}
